import React from "react";
import { Link } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import slugify from "slugify";
import { toAppPath } from "#/lib/router-links";

type Props = {
  source: string;
};

const components: Components = {
  a: ({ children, href }) => {
    const safeHref = href ?? "#";

    const isExternalLink = (url: string) =>
      /^(https?:\/\/)/.test(url) && !url.includes("roamdoc.com");

    if (isExternalLink(safeHref)) {
      return (
        <a href={safeHref} rel="noopener noreferrer nofollow" target="_blank">
          {children}
        </a>
      );
    }

    return <Link {...toAppPath(safeHref)}>{children}</Link>;
  },
  h2: ({ children, ...props }) => {
    const id = slugify(flattenChildren(children), { lower: true });
    return (
      <h2
        className="mb-5 mt-16 text-3xl font-bold text-sky-600"
        id={id}
        {...props}
      >
        {children}
      </h2>
    );
  },
  h3: ({ children, ...props }) => {
    const id = slugify(flattenChildren(children), { lower: true });

    return (
      <h3 className="mt-5 text-xl font-bold" id={id} {...props}>
        {children}
      </h3>
    );
  },
  h4: ({ children, ...props }) => {
    const id = slugify(flattenChildren(children), { lower: true });
    return (
      <h4 className="mt-5 font-bold " id={id} {...props}>
        {children}
      </h4>
    );
  },
};

export default function Mdx({ source }: Props) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {source}
    </ReactMarkdown>
  );
}

function flattenChildren(children: React.ReactNode) {
  return React.Children.toArray(children)
    .map((child) => (typeof child === "string" ? child : ""))
    .join(" ");
}
